import type React from "react";
import { ArrowLeft, ArrowRight, Loader2, RotateCcw, X } from "lucide-react";
import { useState } from "react";
import { useArticleStore } from "../store/useArticleStore";
import { updateArticle } from "../utils/updateArticle";

export const PhotoPreview: React.FC = () => {
  const { article, setPhotos, setArticle } = useArticleStore();
  const [retryingPhotoId, setRetryingPhotoId] = useState<string>();

  const removePhoto = (id: string) => {
    setPhotos(article.sections.filter((photo) => photo.id !== id));
  };

  const movePhoto = (index: number, delta: number) => {
    const destination = index + delta;
    if (destination < 0 || destination >= article.sections.length) return;
    const photos = [...article.sections];
    [photos[index], photos[destination]] = [photos[destination], photos[index]];
    setPhotos(photos);
  };

  const retryPhoto = async (id: string) => {
    setRetryingPhotoId(id);
    try {
      await updateArticle(article, setArticle, { photoIds: [id] });
    } catch {
      // updateArticle records a user-facing error in the store.
    } finally {
      setRetryingPhotoId(undefined);
    }
  };

  if (article.sections.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
      {article.sections.map((photo, index) => (
        <div key={photo.id} className="relative group">
          {photo.preview ? (
            <>
              <img
                src={photo.preview}
                alt={photo.title || "Uploaded photo"}
                className={`w-full h-32 object-cover rounded-lg ${
                  photo.isProcessing ? "opacity-50" : ""
                }`}
                onError={(e) => {
                  // If the preview fails to load, try to create a new one
                  const target = e.target as HTMLImageElement;
                  if (photo.file && target.src.startsWith("blob:")) {
                    const newPreview = URL.createObjectURL(photo.file);
                    target.src = newPreview;
                    // Update the preview in the store
                    setPhotos(
                      article.sections.map((p) =>
                        p.id === photo.id ? { ...p, preview: newPreview } : p,
                      ),
                    );
                  }
                }}
              />
              {photo.isProcessing && (
                <div
                  className="absolute inset-0 flex items-center justify-center"
                  aria-live="polite"
                >
                  <Loader2 aria-hidden="true" className="animate-spin h-8 w-8 text-blue-500" />
                  <span className="sr-only">Processing {photo.file.name}</span>
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center">
              <span className="text-gray-400">Loading...</span>
            </div>
          )}
          <div className="absolute top-2 right-2 flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <button
              type="button"
              aria-label={`Move ${photo.file.name} earlier`}
              title="Move earlier"
              disabled={index === 0 || article.isLoading}
              onClick={() => movePhoto(index, -1)}
              className="p-1.5 bg-white rounded-full shadow-md disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <ArrowLeft aria-hidden="true" className="h-4 w-4 text-gray-600" />
            </button>
            <button
              type="button"
              aria-label={`Move ${photo.file.name} later`}
              title="Move later"
              disabled={index === article.sections.length - 1 || article.isLoading}
              onClick={() => movePhoto(index, 1)}
              className="p-1.5 bg-white rounded-full shadow-md disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <ArrowRight aria-hidden="true" className="h-4 w-4 text-gray-600" />
            </button>
            <button
              type="button"
              aria-label={`Remove ${photo.file.name}`}
              title="Remove photo"
              disabled={article.isLoading}
              onClick={() => removePhoto(photo.id)}
              className="p-1.5 bg-white rounded-full shadow-md disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <X aria-hidden="true" className="h-4 w-4 text-gray-600" />
            </button>
          </div>
          {photo.error && (
            <div className="absolute inset-x-1 bottom-1 rounded-md bg-red-700/95 p-2 text-xs text-white">
              <p className="line-clamp-2">{photo.error}</p>
              <button
                type="button"
                aria-label={`Retry ${photo.file.name}`}
                onClick={() => retryPhoto(photo.id)}
                disabled={article.isLoading || retryingPhotoId === photo.id}
                className="mt-1 inline-flex items-center gap-1 font-semibold underline disabled:opacity-60"
              >
                <RotateCcw aria-hidden="true" className="h-3 w-3" />
                Retry
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
